import React, { useState, useEffect } from "react";
import Modal from "../../components/Modal";
import { ShiftConfigPayload, DayOfWeek } from "../../util/types";
import { validateNoLeadingTrailingWhitespace } from "util/validation";

interface ShiftFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpdate: (
    shifts: ShiftConfigPayload[],
    applySameForWholeWeek: boolean,
  ) => void;
  day: DayOfWeek;
  existingShifts: ShiftConfigPayload[];
}

const ShiftFormModal: React.FC<ShiftFormModalProps> = ({
  isOpen,
  onClose,
  onUpdate,
  day,
  existingShifts,
}) => {
  const [shifts, setShifts] = useState<ShiftConfigPayload[]>([]);
  const [applySameForWholeWeek, setApplySameForWholeWeek] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Added: Auto-dismiss the error toast after 4 seconds
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        setError(null);
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  useEffect(() => {
    if (isOpen) {
      if (existingShifts.length > 0) {
        setShifts(existingShifts.map((s) => ({ ...s, day })));
      } else {
        // Default: Add one empty shift with "Both Genders Allowed" as default
        setShifts([
          {
            day,
            shiftName: "Shift 1",
            startHour: 6,
            endHour: 14,
            workVolume: 70,
            requiredAgents: 6,
            onlyMale: false,
            onlyFemale: false,
            bothGenderAllowed: true,
          },
        ]);
      }
      setApplySameForWholeWeek(false);
      setError(null);
    }
  }, [isOpen, existingShifts, day]);

  const handleAddShift = () => {
    const newShiftNumber = shifts.length + 1;
    setShifts([
      ...shifts,
      {
        day,
        shiftName: `Shift ${newShiftNumber}`,
        startHour: 14,
        endHour: 22,
        workVolume: 100,
        requiredAgents: 10,
        onlyMale: false,
        onlyFemale: false,
        bothGenderAllowed: true,
      },
    ]);
  };

  const handleRemoveShift = (index: number) => {
    if (shifts.length > 1) {
      setShifts(shifts.filter((_, i) => i !== index));
    }
  };

  const handleShiftChange = (
    index: number,
    field: keyof ShiftConfigPayload,
    value: string | number,
  ) => {
    const updatedShifts = [...shifts];
    updatedShifts[index] = { ...updatedShifts[index], [field]: value };
    setShifts(updatedShifts);
  };

  const handleGenderChange = (
    index: number,
    preference: "onlyMale" | "onlyFemale" | "both",
  ) => {
    const updatedShifts = [...shifts];
    updatedShifts[index] = {
      ...updatedShifts[index],
      onlyMale: preference === "onlyMale",
      onlyFemale: preference === "onlyFemale",
      bothGenderAllowed: preference === "both",
    };
    setShifts(updatedShifts);
  };

  // Helper function to check if a shift is overnight (crosses midnight)
  const isOvernightShift = (shift: ShiftConfigPayload): boolean => {
    return shift.endHour < shift.startHour;
  };

  // Helper function to check if two shifts overlap
  const doShiftsOverlap = (
    shift1: ShiftConfigPayload,
    shift2: ShiftConfigPayload,
  ): boolean => {
    const isShift1Overnight = isOvernightShift(shift1);
    const isShift2Overnight = isOvernightShift(shift2);

    // Case 1: Both shifts are overnight
    if (isShift1Overnight && isShift2Overnight) {
      return true; // Two overnight shifts always overlap
    }

    // Case 2: shift1 is overnight, shift2 is not
    if (isShift1Overnight && !isShift2Overnight) {
      // Overnight shift covers [start->24] and [0->end]
      return (
        shift2.startHour >= shift1.startHour || shift2.endHour <= shift1.endHour
      );
    }

    // Case 3: shift2 is overnight, shift1 is not
    if (!isShift1Overnight && isShift2Overnight) {
      // Overnight shift covers [start->24] and [0->end]
      return (
        shift1.startHour >= shift2.startHour || shift1.endHour <= shift2.endHour
      );
    }

    // Case 4: Neither shift is overnight (standard check)
    return (
      shift1.startHour < shift2.endHour && shift2.startHour < shift1.endHour
    );
  };

  // Check all shifts for overlaps
  const checkShiftOverlaps = (
    shiftsToCheck: ShiftConfigPayload[],
  ): string[] => {
    const errors: string[] = [];

    for (let i = 0; i < shiftsToCheck.length; i++) {
      for (let j = i + 1; j < shiftsToCheck.length; j++) {
        if (doShiftsOverlap(shiftsToCheck[i], shiftsToCheck[j])) {
          const shift1Display = isOvernightShift(shiftsToCheck[i])
            ? `${shiftsToCheck[i].startHour}:00-${shiftsToCheck[i].endHour}:00 (overnight)`
            : `${shiftsToCheck[i].startHour}:00-${shiftsToCheck[i].endHour}:00`;
          const shift2Display = isOvernightShift(shiftsToCheck[j])
            ? `${shiftsToCheck[j].startHour}:00-${shiftsToCheck[j].endHour}:00 (overnight)`
            : `${shiftsToCheck[j].startHour}:00-${shiftsToCheck[j].endHour}:00`;

          errors.push(
            `"${shiftsToCheck[i].shiftName}" (${shift1Display}) overlaps with "${shiftsToCheck[j].shiftName}" (${shift2Display})`,
          );
        }
      }
    }

    return errors;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validation: Check if all fields are filled
    for (const shift of shifts) {
      if (
        !shift.shiftName ||
        shift.startHour < 0 ||
        shift.endHour < 0 ||
        shift.requiredAgents < 1
      ) {
        setError("Please fill all fields correctly");
        return;
      }

      // Validation: Check for empty spaces and leading/trailing whitespaces
      if (!validateNoLeadingTrailingWhitespace(shift.shiftName)) {
        setError(
          `Shift name cannot be empty spaces or have leading/trailing whitespaces`
        );
        return;
      }

      // Validate hour ranges (0-23)
      if (shift.startHour > 23 || shift.endHour > 23) {
        setError(`"${shift.shiftName}": Hours must be between 0 and 23`);
        return;
      }

      // Allow overnight shifts (e.g., 22-6), but prevent same start and end hour
      if (shift.startHour === shift.endHour) {
        setError(
          `"${shift.shiftName}": Start hour and end hour cannot be the same`,
        );
        return;
      }
    }

    // Validation: Check for duplicate shift names
    const shiftNames = shifts.map((s) => s.shiftName.trim().toLowerCase());
    const duplicateNames = shiftNames.filter(
      (name, index) => shiftNames.indexOf(name) !== index,
    );
    if (duplicateNames.length > 0) {
      const uniqueDuplicates = [...new Set(duplicateNames)];
      const duplicateNamesDisplay = shifts
        .filter((s) =>
          uniqueDuplicates.includes(s.shiftName.trim().toLowerCase()),
        )
        .map((s) => `"${s.shiftName}"`)
        .filter((name, index, self) => self.indexOf(name) === index)
        .join(", ");
      setError(
        `⚠️ Duplicate shift names detected: ${duplicateNamesDisplay}. Each shift must have a unique name.`,
      );
      return;
    }

    // Validation: Check for overlapping shifts
    const overlappingShifts = checkShiftOverlaps(shifts);
    if (overlappingShifts.length > 0) {
      const errorMessage = `⚠️ Time overlap detected:\n${overlappingShifts
        .map((err) => `• ${err}`)
        .join("\n")}`;
      setError(errorMessage);
      return;
    }

    // Update parent state (local changes only)
    onUpdate(shifts, applySameForWholeWeek);
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      header={`Configure Shifts for ${day}`}
    >
      <form onSubmit={handleSubmit}>
        {/* Updated: Added fixed positioning and shadow to turn this into a toast */}
        {error && (
          <div className="fixed top-4 right-4 z-[9999] shadow-xl text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900 p-3 rounded-lg text-sm border border-red-200 dark:border-red-800 transition-all duration-300">
            <div className="whitespace-pre-line">{error}</div>
          </div>
        )}

        <div className="mb-3">
          <div className="flex items-center">
            <input
              type="checkbox"
              checked={applySameForWholeWeek}
              onChange={(e) => setApplySameForWholeWeek(e.target.checked)}
              className="h-3.5 w-3.5 rounded border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-0 focus:outline-none focus:ring-offset-0 dark:bg-gray-700 cursor-pointer"
            />
            <span className="ml-2 text-xs text-gray-700 dark:text-gray-300 font-medium">
              Apply same shifts for entire week
            </span>
          </div>
        </div>

        <div className="space-y-3 max-h-96 overflow-y-auto pr-2">
          {shifts.map((shift, index) => (
            <div
              key={index}
              className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 bg-gray-50 dark:bg-gray-800"
            >
              <div className="flex justify-between items-center mb-2">
                <h4 className="font-semibold text-gray-700 dark:text-gray-200 text-sm">
                  Shift {index + 1}
                </h4>
                {shifts.length > 1 && (
                  <button
                    type="button"
                    onClick={() => handleRemoveShift(index)}
                    className="text-red-500 hover:text-red-700 text-xs"
                  >
                    Remove
                  </button>
                )}
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Shift Name
                  </label>
                  <input
                    type="text"
                    value={shift.shiftName}
                    onChange={(e) => {
                      // 1. Strip any character that is NOT a letter, digit, space, or hyphen
                      const sanitizedValue = e.target.value.replace(/[^a-zA-Z0-9\s-]/g, "");

                      // 2. Pass the sanitized value to your state handler
                      handleShiftChange(index, "shiftName", sanitizedValue);
                    }}
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-700 dark:text-gray-100"
                    required
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Start Hour
                  </label>
                  <select
                    value={shift.startHour}
                    onChange={(e) =>
                      handleShiftChange(
                        index,
                        "startHour",
                        parseInt(e.target.value),
                      )
                    }
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-700 dark:text-gray-100"
                    required
                  >
                    <option value={0}>00:00 (12 AM)</option>
                    <option value={1}>01:00 (1 AM)</option>
                    <option value={2}>02:00 (2 AM)</option>
                    <option value={3}>03:00 (3 AM)</option>
                    <option value={4}>04:00 (4 AM)</option>
                    <option value={5}>05:00 (5 AM)</option>
                    <option value={6}>06:00 (6 AM)</option>
                    <option value={7}>07:00 (7 AM)</option>
                    <option value={8}>08:00 (8 AM)</option>
                    <option value={9}>09:00 (9 AM)</option>
                    <option value={10}>10:00 (10 AM)</option>
                    <option value={11}>11:00 (11 AM)</option>
                    <option value={12}>12:00 (12 PM)</option>
                    <option value={13}>13:00 (1 PM)</option>
                    <option value={14}>14:00 (2 PM)</option>
                    <option value={15}>15:00 (3 PM)</option>
                    <option value={16}>16:00 (4 PM)</option>
                    <option value={17}>17:00 (5 PM)</option>
                    <option value={18}>18:00 (6 PM)</option>
                    <option value={19}>19:00 (7 PM)</option>
                    <option value={20}>20:00 (8 PM)</option>
                    <option value={21}>21:00 (9 PM)</option>
                    <option value={22}>22:00 (10 PM)</option>
                    <option value={23}>23:00 (11 PM)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                    End Hour
                  </label>
                  <select
                    value={shift.endHour}
                    onChange={(e) =>
                      handleShiftChange(
                        index,
                        "endHour",
                        parseInt(e.target.value),
                      )
                    }
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-700 dark:text-gray-100"
                    required
                  >
                    <option value={0}>00:00 (12 AM)</option>
                    <option value={1}>01:00 (1 AM)</option>
                    <option value={2}>02:00 (2 AM)</option>
                    <option value={3}>03:00 (3 AM)</option>
                    <option value={4}>04:00 (4 AM)</option>
                    <option value={5}>05:00 (5 AM)</option>
                    <option value={6}>06:00 (6 AM)</option>
                    <option value={7}>07:00 (7 AM)</option>
                    <option value={8}>08:00 (8 AM)</option>
                    <option value={9}>09:00 (9 AM)</option>
                    <option value={10}>10:00 (10 AM)</option>
                    <option value={11}>11:00 (11 AM)</option>
                    <option value={12}>12:00 (12 PM)</option>
                    <option value={13}>13:00 (1 PM)</option>
                    <option value={14}>14:00 (2 PM)</option>
                    <option value={15}>15:00 (3 PM)</option>
                    <option value={16}>16:00 (4 PM)</option>
                    <option value={17}>17:00 (5 PM)</option>
                    <option value={18}>18:00 (6 PM)</option>
                    <option value={19}>19:00 (7 PM)</option>
                    <option value={20}>20:00 (8 PM)</option>
                    <option value={21}>21:00 (9 PM)</option>
                    <option value={22}>22:00 (10 PM)</option>
                    <option value={23}>23:00 (11 PM)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Work Volume
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={shift.workVolume}
                    onChange={(e) =>
                      handleShiftChange(
                        index,
                        "workVolume",
                        parseInt(e.target.value),
                      )
                    }
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-700 dark:text-gray-100"
                    required
                    onWheel={(e) => (e.target as HTMLInputElement).blur()}
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Required Agents
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={shift.requiredAgents}
                    onChange={(e) =>
                      handleShiftChange(
                        index,
                        "requiredAgents",
                        parseInt(e.target.value),
                      )
                    }
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-700 dark:text-gray-100"
                    required
                    onWheel={(e) => (e.target as HTMLInputElement).blur()}
                  />
                </div>

                <div className="col-span-2">
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Gender Preference
                  </label>
                  <div className="flex gap-4">
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        name={`gender-${index}`}
                        checked={shift.onlyMale}
                        onChange={() => handleGenderChange(index, "onlyMale")}
                        className="h-4 w-4 text-indigo-600 focus:ring-0 focus:outline-none focus:ring-offset-0 cursor-pointer dark:bg-gray-700 dark:border-gray-600"
                      />
                      <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                        Only Male
                      </span>
                    </label>

                    <label className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        name={`gender-${index}`}
                        checked={shift.onlyFemale}
                        onChange={() => handleGenderChange(index, "onlyFemale")}
                        className="h-4 w-4 text-indigo-600 focus:ring-0 focus:outline-none focus:ring-offset-0 cursor-pointer dark:bg-gray-700 dark:border-gray-600"
                      />
                      <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                        Only Female
                      </span>
                    </label>

                    <label className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        name={`gender-${index}`}
                        checked={shift.bothGenderAllowed}
                        onChange={() => handleGenderChange(index, "both")}
                        className="h-4 w-4 text-indigo-600 focus:ring-0 focus:outline-none focus:ring-offset-0 cursor-pointer dark:bg-gray-700 dark:border-gray-600"
                      />
                      <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                        Both Allowed
                      </span>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <button
          type="button"
          onClick={handleAddShift}
          className="mt-3 w-full bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 font-semibold px-3 py-1.5 text-sm rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors duration-200 flex items-center justify-center"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4 mr-1.5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 4v16m8-8H4"
            />
          </svg>
          Add Another Shift
        </button>

        <div className="mt-6 flex justify-end space-x-3">
          <button
            type="button"
            onClick={onClose}
            className="bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 font-semibold px-3 py-1.5 text-sm rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors duration-200"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="bg-indigo-600 text-white font-semibold px-3 py-1.5 text-sm rounded-lg shadow-md hover:bg-indigo-700 transition-colors duration-200"
          >
            {applySameForWholeWeek ? "Apply to All Days" : "Apply Changes"}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default ShiftFormModal;